/**
 * /links — payment links (Stripe's Payment Links and Buy Button).
 *
 * A link bundles products from /products with the checkout options: can the
 * buyer change the quantity, do we ask for an email or a shipping address,
 * and what happens after payment. Share the URL anywhere, or paste the buy
 * button on a website; either way the price comes from the catalog.
 */

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "../lib/api";
import {
  buyButtonSnippet,
  createPaymentLink,
  listPaymentLinks,
  listProducts,
  priceLabel,
  updatePaymentLink,
  usd,
  type PaymentLink,
  type PaymentLinkInput,
  type Product,
} from "../lib/catalog";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";
import { inputStyle, labelStyle } from "./products";

const gatewayOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch {
    return "https://shieldz.cash";
  }
};

export function PaymentLinks() {
  const [items, setItems] = useState<PaymentLink[] | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PaymentLink | "new" | null>(null);

  const refetch = async () => {
    try {
      const [l, p] = await Promise.all([listPaymentLinks(), listProducts()]);
      setItems(l.data);
      setProducts(p.data.filter((x) => x.active && x.default_price));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load payment links.");
    }
  };
  useEffect(() => {
    void refetch();
  }, []);

  const noProducts = products !== null && products.length === 0;

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 760 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Payment links</h2>
            <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "4px 0 0" }}>
              Share a link or put a buy button on your website. Prices come from your{" "}
              <Link to="/products" style={{ color: "var(--brand-700)" }}>Products</Link>.
            </p>
          </div>
          {editing === null && !noProducts && (
            <Btn onClick={() => setEditing("new")} disabled={products === null}>
              <Ic name="plus" size={14} /> New payment link
            </Btn>
          )}
        </div>

        {error && <Banner tone="fail">{error}</Banner>}

        {noProducts && (
          <div className="card" style={{ padding: 24, marginBottom: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220, fontSize: 14, color: "var(--ink-2)" }}>
              Add a product first. A payment link sells one or more of your products.
            </div>
            <Link to="/products" className="btn btn-primary">
              <Ic name="plus" size={14} /> Add a product
            </Link>
          </div>
        )}

        {editing !== null && products && (
          <LinkForm
            link={editing === "new" ? null : editing}
            products={products}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void refetch();
            }}
          />
        )}

        {items === null ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>
        ) : items.length === 0 && editing === null ? (
          !noProducts && (
            <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>No payment links yet.</div>
              <div style={{ fontSize: 12.5 }}>Make one to sell a product with a link or a buy button.</div>
            </div>
          )
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((l) => (
              <LinkRow key={l.id} item={l} onEdit={() => setEditing(l)} onChange={refetch} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CopyBtn({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Btn
      variant="ghost"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        } catch {
          /* ignore */
        }
      }}
    >
      {copied ? <><Ic name="check" size={14} /> Copied</> : label}
    </Btn>
  );
}

const linkTitle = (l: PaymentLink): string =>
  l.line_items.map((li) => `${li.product?.name ?? "Removed product"}${li.quantity > 1 ? ` × ${li.quantity}` : ""}`).join(", ");

const linkPrice = (l: PaymentLink): string => {
  if (l.line_items.some((li) => li.price?.unit_amount_usd_cents == null)) {
    return l.line_items.length === 1 ? priceLabel(l.line_items[0]!.price) : "Customer chooses";
  }
  const total = l.line_items.reduce((s, li) => s + (li.price?.unit_amount_usd_cents ?? 0) * li.quantity, 0);
  return usd(total);
};

// The webhook name for a question: "Panel username" -> panel_username.
const fieldKey = (label: string) =>
  label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "field";

function LinkRow({ item, onEdit, onChange }: { item: PaymentLink; onEdit: () => void; onChange: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const stale = item.line_items.some((li) => !li.product?.active || !li.price?.active);
  const options = [
    item.line_items.some((li) => li.adjustable_quantity.enabled) && "Buyer picks quantity",
    item.collect_email && "Asks for email",
    item.collect_shipping_address && "Asks for shipping address",
    ...(item.custom_fields ?? []).map((f) => `Asks for ${f.label.custom}`),
    item.after_completion.type === "redirect" && "Sends buyer to your site",
  ].filter(Boolean) as string[];
  const snippet = buyButtonSnippet(gatewayOrigin(item.url), item.id);

  const toggle = async () => {
    if (item.active && !confirm("Turn this link off? Buyers who open it will see that it's no longer available.")) return;
    setBusy(true);
    try {
      await updatePaymentLink(item.id, { active: !item.active });
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, opacity: item.active ? 1 : 0.6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {linkTitle(item)}
            {!item.active && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginLeft: 8 }}>Off</span>}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 3 }}>
            {item.paid_count > 0 ? `${item.paid_count} paid · ${usd(item.paid_usd_cents)}` : "No payments yet"}
            {options.length > 0 && ` · ${options.join(" · ")}`}
          </div>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: "nowrap" }}>{linkPrice(item)}</div>
      </div>
      {stale && item.active && (
        <div style={{ marginTop: 10 }}>
          <Banner tone="warn">A product in this link is archived, so the link isn't selling. Restore it on Products or edit the link.</Banner>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          readOnly
          value={item.url}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minWidth: 180,
            fontSize: 12.5,
            fontFamily: "var(--mono, monospace)",
            padding: "9px 11px",
            border: "1px solid var(--line, #e6e8eb)",
            borderRadius: 9,
            background: "var(--surface-2, #fafbfc)",
            color: "var(--ink-2, #374151)",
          }}
        />
        <CopyBtn text={item.url} label="Copy link" />
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          <Btn variant="ghost">Open</Btn>
        </a>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <Btn variant="ghost" onClick={() => setShowCode((v) => !v)}>
          <Ic name="code" size={14} /> {showCode ? "Hide buy button" : "Buy button"}
        </Btn>
        <Btn variant="ghost" onClick={onEdit} disabled={busy}>Edit</Btn>
        <Btn variant="ghost" onClick={() => void toggle()} disabled={busy}>{item.active ? "Turn off" : "Turn on"}</Btn>
      </div>
      {showCode && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginBottom: 6 }}>
            Paste this on your website where the product is. It opens the checkout right on your page.
          </div>
          <pre
            style={{
              margin: 0,
              padding: "12px 14px",
              background: "var(--surface-2, #f5f6f7)",
              borderRadius: 10,
              fontSize: 12.5,
              overflowX: "auto",
              fontFamily: "var(--mono, monospace)",
            }}
          >
            {snippet}
          </pre>
          <div style={{ marginTop: 8 }}>
            <CopyBtn text={snippet} label="Copy code" />
          </div>
        </div>
      )}
    </div>
  );
}

type Line = { product: string; quantity: string; adjustable: boolean; min: string; max: string };

function LinkForm({
  link,
  products,
  onClose,
  onSaved,
}: {
  link: PaymentLink | null;
  products: Product[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [lines, setLines] = useState<Line[]>(
    link
      ? link.line_items.map((li) => ({
          product: products.find((p) => p.default_price?.id === li.price?.id)?.id ?? li.product?.id ?? "",
          quantity: String(li.quantity),
          adjustable: li.adjustable_quantity.enabled,
          min: String(li.adjustable_quantity.minimum ?? 1),
          max: String(li.adjustable_quantity.maximum ?? 10),
        }))
      : [{ product: products[0]?.id ?? "", quantity: "1", adjustable: true, min: "1", max: "10" }],
  );
  const [collectEmail, setCollectEmail] = useState(link?.collect_email ?? false);
  const [collectShipping, setCollectShipping] = useState(link?.collect_shipping_address ?? false);
  const [fields, setFields] = useState<Array<{ key: string; label: string; optional: boolean }>>(
    (link?.custom_fields ?? []).map((f) => ({ key: f.key, label: f.label.custom, optional: f.optional })),
  );
  const [after, setAfter] = useState<"message" | "redirect">(link?.after_completion.type === "redirect" ? "redirect" : "message");
  const [message, setMessage] = useState(
    link?.after_completion.type === "hosted_confirmation" ? (link.after_completion.hosted_confirmation.custom_message ?? "") : "",
  );
  const [url, setUrl] = useState(link?.after_completion.type === "redirect" ? link.after_completion.redirect.url : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const productOf = (id: string) => products.find((p) => p.id === id) ?? null;
  const isCustom = (id: string) => productOf(id)?.default_price?.unit_amount_usd_cents == null;
  const set = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  const total = lines.reduce((s, l) => {
    const unit = productOf(l.product)?.default_price?.unit_amount_usd_cents;
    return unit == null ? s : s + unit * (Number(l.quantity) || 0);
  }, 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const items: PaymentLinkInput["line_items"] = [];
    for (const l of lines) {
      const p = productOf(l.product);
      if (!p?.default_price) return setErr("Pick a product for every line.");
      const custom = isCustom(l.product);
      const q = custom ? 1 : Number(l.quantity);
      if (!Number.isInteger(q) || q < 1 || q > 999) return setErr(`Quantity for ${p.name} must be a whole number from 1 to 999.`);
      let adjustable: PaymentLinkInput["line_items"][number]["adjustable_quantity"];
      if (!custom && l.adjustable) {
        const lo = Number(l.min);
        const hi = Number(l.max);
        if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 0 || hi < 1 || lo > hi || hi > 999 || q < lo || q > hi) {
          return setErr(`For ${p.name}, the quantity range must include ${q} and stay between 0 and 999.`);
        }
        adjustable = { enabled: true, minimum: lo, maximum: hi };
      }
      items.push({ price: p.default_price.id, quantity: q, ...(adjustable ? { adjustable_quantity: adjustable } : {}) });
    }
    if (new Set(items.map((i) => i.price)).size !== items.length) return setErr("Each product can be in the link once.");
    if (after === "redirect" && !/^https:\/\//.test(url.trim())) return setErr("The page to send buyers to must start with https://");
    const customFields: NonNullable<PaymentLinkInput["custom_fields"]> = [];
    for (const f of fields) {
      const label = f.label.trim();
      if (!label) return setErr("Write the question, or remove the empty one.");
      // Keep an existing key so the webhook field names don't change on edit.
      let key = f.key || fieldKey(label);
      while (customFields.some((x) => x.key === key)) key = `${key.slice(0, 37)}_${customFields.length + 1}`;
      customFields.push({ key, label, optional: f.optional });
    }
    const body: PaymentLinkInput = {
      line_items: items,
      collect_email: collectEmail,
      collect_shipping_address: collectShipping,
      custom_fields: customFields,
      after_completion:
        after === "redirect"
          ? { type: "redirect", redirect: { url: url.trim() } }
          : { type: "hosted_confirmation", hosted_confirmation: { custom_message: message.trim() || null } },
    };
    setBusy(true);
    try {
      if (link) await updatePaymentLink(link.id, body);
      else await createPaymentLink(body);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not save the payment link.");
      setBusy(false);
    }
  };

  const check = (checked: boolean, onChange: (v: boolean) => void, label: string, hint?: string) => (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12, fontSize: 13.5, color: "var(--ink-2)", cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <span>
        {label}
        {hint && <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>{hint}</span>}
      </span>
    </label>
  );

  return (
    <form className="card" style={{ padding: 20, marginBottom: 14 }} onSubmit={submit}>
      <div style={{ fontWeight: 600, fontSize: 16 }}>{link ? "Edit payment link" : "New payment link"}</div>
      {link && (
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4 }}>The link address stays the same.</div>
      )}

      <label style={labelStyle}>Products</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
        {lines.map((l, i) => {
          const custom = isCustom(l.product);
          return (
            <div key={i} style={{ border: "1px solid var(--line, #e6e8eb)", borderRadius: 12, padding: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select style={{ ...inputStyle, marginTop: 0, flex: 1 }} value={l.product} onChange={(e) => set(i, { product: e.target.value })}>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} · {priceLabel(p.default_price)}</option>
                  ))}
                </select>
                {!custom && (
                  <input
                    style={{ ...inputStyle, marginTop: 0, width: 72 }}
                    inputMode="numeric"
                    aria-label="Quantity"
                    value={l.quantity}
                    onChange={(e) => set(i, { quantity: e.target.value })}
                  />
                )}
                {lines.length > 1 && (
                  <button type="button" className="btn btn-ghost" aria-label="Remove" onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}>
                    <Ic name="x" size={14} />
                  </button>
                )}
              </div>
              {custom ? (
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8 }}>The buyer types the amount.</div>
              ) : (
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap", fontSize: 13, color: "var(--ink-2)" }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={l.adjustable} onChange={(e) => set(i, { adjustable: e.target.checked })} />
                    Buyer can change the quantity
                  </label>
                  {l.adjustable && (
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      from
                      <input style={{ ...inputStyle, marginTop: 0, width: 58, padding: "6px 8px" }} inputMode="numeric" value={l.min} onChange={(e) => set(i, { min: e.target.value })} aria-label="Lowest quantity" />
                      to
                      <input style={{ ...inputStyle, marginTop: 0, width: 58, padding: "6px 8px" }} inputMode="numeric" value={l.max} onChange={(e) => set(i, { max: e.target.value })} aria-label="Highest quantity" />
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {lines.length < 20 && products.length > lines.length && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: 8 }}
          onClick={() =>
            setLines((ls) => [
              ...ls,
              { product: products.find((p) => !ls.some((x) => x.product === p.id))?.id ?? products[0]!.id, quantity: "1", adjustable: false, min: "1", max: "10" },
            ])
          }
        >
          <Ic name="plus" size={14} /> Add another product
        </button>
      )}
      {total > 0 && (
        <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 10 }}>
          Starts at <strong>{usd(total)}</strong>
        </div>
      )}

      <label style={labelStyle}>Ask the buyer for</label>
      {check(collectEmail, setCollectEmail, "Email address", "For receipts and order updates. You'll see it in Activity.")}
      {check(collectShipping, setCollectShipping, "Shipping address", "For physical goods. Shown in Activity and sent in the webhook.")}
      {fields.map((f, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
          <input
            style={{ ...inputStyle, marginTop: 0, flex: 1 }}
            maxLength={50}
            value={f.label}
            placeholder="Your username on our site"
            aria-label="Question"
            onChange={(e) => setFields((fs) => fs.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))}
          />
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, color: "var(--ink-2)", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={f.optional}
              onChange={(e) => setFields((fs) => fs.map((x, k) => (k === i ? { ...x, optional: e.target.checked } : x)))}
            />
            Optional
          </label>
          <button type="button" className="btn btn-ghost" aria-label="Remove question" onClick={() => setFields((fs) => fs.filter((_, k) => k !== i))}>
            <Ic name="x" size={14} />
          </button>
        </div>
      ))}
      {fields.length < 3 && (
        <button type="button" className="btn btn-ghost" style={{ marginTop: 8 }} onClick={() => setFields((fs) => [...fs, { key: "", label: "", optional: false }])}>
          <Ic name="plus" size={14} /> Ask a question
        </button>
      )}
      {fields.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
          The answer shows in Activity, in your Telegram and Discord alerts, and in the webhook. To fill it in for the buyer, add <code>?cf_{fields[0]!.key || fieldKey(fields[0]!.label)}=…</code> to the link.
        </div>
      )}

      <label style={labelStyle}>After payment</label>
      <div className="set-seg" style={{ marginTop: 6 }}>
        <button type="button" className={`set-seg-opt${after === "message" ? " on" : ""}`} aria-pressed={after === "message"} onClick={() => setAfter("message")}>
          Show a thank-you note
        </button>
        <button type="button" className={`set-seg-opt${after === "redirect" ? " on" : ""}`} aria-pressed={after === "redirect"} onClick={() => setAfter("redirect")}>
          Send them to my website
        </button>
      </div>
      {after === "message" ? (
        <textarea
          style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
          maxLength={500}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Thanks! We'll ship your order within 2 days. (optional)"
        />
      ) : (
        <input style={inputStyle} type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://myshop.com/thank-you" />
      )}

      {err && (
        <div style={{ marginTop: 12 }}>
          <Banner tone="fail">{err}</Banner>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Btn type="submit" loading={busy} disabled={busy}>
          {link ? "Save" : "Create payment link"}
        </Btn>
        <Btn type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}
